import React from 'react';

import {
  AssigneeRole,
  type FirmRoleAssignmentDetailWithConfig,
  useListFirmRoleAssignmentsQuery,
} from '@/services/api/firmsService';

import { AddRoleAssignmentDialog } from './AddRoleAssignmentDialog';
import { DeleteRoleAssignmentDialog } from './DeleteRoleAssignmentDialog';
import { EditRoleAssignmentDialog } from './EditRoleAssignmentDialog';

type Props = {
  firmId: string;
};

// Convert snake_case to Title Case
const formatRoleName = (role: AssigneeRole): string => {
  return role
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const FirmRoleAssignments: React.FC<Props> = ({ firmId }) => {
  const {
    data: roleAssignments = [],
    refetch,
    isLoading,
  } = useListFirmRoleAssignmentsQuery({ firmPk: firmId }, { skip: !firmId });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Role Assignments</h2>
        <AddRoleAssignmentDialog firmId={firmId} onCreated={() => refetch()} />
      </div>
      <div className="border rounded-md">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left">Role</th>
              <th className="px-3 py-2 text-left">Assigned To</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td className="px-3 py-3" colSpan={3}>
                  Loading...
                </td>
              </tr>
            ) : roleAssignments.length === 0 ? (
              <tr>
                <td className="px-3 py-3" colSpan={3}>
                  No role assignments yet
                </td>
              </tr>
            ) : (
              roleAssignments.map((assignment) => {
                const typedAssignment =
                  assignment as FirmRoleAssignmentDetailWithConfig;
                const showAutoAssignBadge =
                  typedAssignment.role === 'case_manager' &&
                  typedAssignment.config?.case_auto_assign;

                return (
                  <tr key={assignment.id} className="border-t">
                    <td className="px-3 py-2">
                      {formatRoleName(assignment.role)}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <span>
                          {assignment.assignee.display_name}
                          {assignment.assignee.email
                            ? ` (${assignment.assignee.email})`
                            : ''}
                        </span>
                        {showAutoAssignBadge && (
                          <span className="inline-flex items-center rounded-full bg-green-50 dark:bg-green-900/30 px-2 py-0.5 text-xs font-medium text-green-700 dark:text-green-300">
                            Auto-assign
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right space-x-2">
                      <EditRoleAssignmentDialog
                        firmId={firmId}
                        assignment={assignment}
                        onUpdated={() => refetch()}
                      />
                      <DeleteRoleAssignmentDialog
                        firmId={firmId}
                        assignment={assignment}
                        onDeleted={() => refetch()}
                      />
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
